import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAccountStore } from '~/stores/account'
import { usePickemApi } from '~/services/api'
import type {
  Tournament,
  LeaderboardPage,
  LeaderboardMe,
  LeaderboardOpenOptions
} from '../types'

// Минимальная длина search-строки. Совпадает с правилом из
// docs/pickem/docs/technical/03-api-basic.md (Лидерборд):
//   «search (опц.) — подстрока никнейма ... минимум 2 символа».
export const LEADERBOARD_SEARCH_MIN_LENGTH = 2
export const LEADERBOARD_PER_PAGE = 15
const SEARCH_DEBOUNCE_MS = 300

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  return (
    (error as { name?: string }).name === 'AbortError' ||
    (error as { code?: number }).code === 20 // DOMException.ABORT_ERR
  )
}

export const useLeaderboardStore = defineStore('pickem-leaderboard', () => {
  const pickemApi = usePickemApi()
  const accountStore = useAccountStore()

  const tournamentId = ref<Tournament['id'] | null>(null)
  const page = ref(1)
  const perPage = ref(LEADERBOARD_PER_PAGE)
  // Сырое значение из инпута (то, что юзер уже напечатал, без debounce).
  const searchInput = ref('')
  // Применённое значение, по которому идёт фактический запрос. Из-за debounce
  // оно отличается от searchInput на ~300ms.
  const appliedSearch = ref('')

  const data = ref<LeaderboardPage | null>(null)
  const me = ref<LeaderboardMe | null>(null)

  const isLoadingPage = ref(false)
  const isLoadingMe = ref(false)
  const error = ref<unknown>(null)

  // Управляющие объекты:
  // - pageController: гасим предыдущий запрос страниц при вводе/смене страницы.
  // - meController: запрос /me — отдельный, тоже отменяемый.
  // - searchTimer: setTimeout debounce. Гасим при следующем нажатии.
  let pageController: AbortController | null = null
  let meController: AbortController | null = null
  let searchTimer: ReturnType<typeof setTimeout> | null = null

  const isSearchActive = computed(
    () => appliedSearch.value.length >= LEADERBOARD_SEARCH_MIN_LENGTH
  )

  const totalPages = computed(() => {
    if (!data.value) return 0
    if (perPage.value <= 0) return 0
    return Math.max(1, Math.ceil(data.value.total / perPage.value))
  })

  const items = computed(() => data.value?.items ?? [])
  const total = computed(() => data.value?.total ?? 0)

  // Юзер виден на текущей странице — нужно подсветить, но не дублировать
  // выше списком. Сравниваем по member.id.
  const meVisibleOnPage = computed(() => {
    if (!me.value) return false
    return items.value.some(row => row.member.id === me.value!.member.id)
  })

  const fetchPage = async (nextPage: number = page.value) => {
    if (!tournamentId.value) return

    pageController?.abort()
    pageController = new AbortController()
    const signal = pageController.signal

    page.value = nextPage
    isLoadingPage.value = true
    error.value = null

    try {
      const query: { page: number; 'per-page': number; search?: string } = {
        page: nextPage,
        'per-page': perPage.value
      }
      if (isSearchActive.value) query.search = appliedSearch.value

      const response = await pickemApi.getLeaderboardPage(
        tournamentId.value,
        query,
        { signal }
      )
      // Если за время запроса успели его абортнуть — игнорируем результат.
      if (signal.aborted) return
      data.value = response
    } catch (e) {
      if (isAbortError(e)) return
      error.value = e
      throw e
    } finally {
      if (!signal.aborted) isLoadingPage.value = false
    }
  }

  const fetchMe = async (id?: Tournament['id']) => {
    const preparedTournamentId = id ?? tournamentId.value
    if (!preparedTournamentId) return

    if (!accountStore.isAuthorized) {
      return
    }

    meController?.abort()
    meController = new AbortController()
    const signal = meController.signal

    isLoadingMe.value = true
    try {
      me.value = await pickemApi.getLeaderboardMe(preparedTournamentId, {
        signal
      })
    } catch (e) {
      if (isAbortError(e)) return
      if (typeof console !== 'undefined') {
        console.warn('[pickem] leaderboard /me failed:', e)
      }
    } finally {
      if (!signal.aborted) isLoadingMe.value = false
    }
  }

  // Открытие списка лидеров. Без options — сброс на первую страницу без фильтра.
  const open = async (
    id: Tournament['id'],
    options?: LeaderboardOpenOptions
  ) => {
    tournamentId.value = id

    const nextSearchInput = options?.search ?? ''
    const trimmedSearch = nextSearchInput.trim()
    const nextAppliedSearch =
      trimmedSearch.length >= LEADERBOARD_SEARCH_MIN_LENGTH ? trimmedSearch : ''

    searchInput.value = nextSearchInput
    appliedSearch.value = nextAppliedSearch
    data.value = null
    await fetchPage(options?.page ?? 1)
  }

  // Применяем поиск с debounce, чтобы каждое нажатие клавиши не било в API.
  // Менее 2 символов — трактуется как «без фильтра».
  const setSearch = (value: string) => {
    searchInput.value = value
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      const next =
        value.trim().length >= LEADERBOARD_SEARCH_MIN_LENGTH ? value.trim() : ''
      if (next === appliedSearch.value) return
      appliedSearch.value = next
      fetchPage(1).catch(() => undefined)
    }, SEARCH_DEBOUNCE_MS)
  }

  const goToPage = (next: number) => {
    if (!totalPages.value) return
    const clamped = Math.max(1, Math.min(totalPages.value, next))
    if (clamped === page.value) return
    fetchPage(clamped).catch(() => undefined)
  }

  const reset = () => {
    if (searchTimer) clearTimeout(searchTimer)
    pageController?.abort()
    meController?.abort()
    pageController = null
    meController = null
    searchTimer = null
    tournamentId.value = null
    page.value = 1
    perPage.value = LEADERBOARD_PER_PAGE
    searchInput.value = ''
    appliedSearch.value = ''
    data.value = null
    me.value = null
    isLoadingPage.value = false
    isLoadingMe.value = false
    error.value = null
  }

  return {
    tournamentId,
    page,
    perPage,
    searchInput,
    appliedSearch,
    isSearchActive,
    isLoadingPage,
    isLoadingMe,
    error,
    items,
    total,
    me,
    totalPages,
    meVisibleOnPage,
    open,
    fetchPage,
    fetchMe,
    setSearch,
    goToPage,
    reset
  }
})
